/** Notification settings: quiet hours (read-write), mandatory escalation (never silenceable), honest delivery states (U099, V08-A12). */
import {
  decodeNotificationsRow,
  decodeQuietHoursRow,
  encodeQuietHoursRequest,
  NOT_SILENCEABLE,
  QUIET_HOURS_PATH,
  type DeliveryRow,
  type QuietHoursRow,
} from "../../contracts/delivery.ts";
import { openConfirmDialog, type DialogHandle } from "../../app/command-dialog-view.ts";
import { badge, instantCell, renderFreshnessBar, scrollRegion } from "../../app/components.ts";
import { h, setChildren } from "../../app/dom.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { clockText, deliveryPresentation, parseClock, windowText } from "../delivery/model.ts";
import { servicesOf, type DeliveryServices } from "../delivery/services.ts";
import { handleOf, makeFrame } from "../frame.ts";
import "../../styles/delivery.css";

function deliveryRow(row: DeliveryRow): HTMLElement {
  const view = deliveryPresentation(row);
  return h(
    "tr",
    {
      attrs: {
        "data-intent": row.intentId,
        "data-state": row.state,
        "data-confirmed": String(view.confirmed),
      },
    },
    h(
      "th",
      { attrs: { scope: "row" } },
      row.recommendationId === "digest"
        ? "Digest"
        : `${row.recommendationId} v${String(row.version)} ${row.direction ?? ""}`.trim(),
    ),
    h("td", {}, row.channel),
    h("td", {}, row.severity),
    h("td", {}, badge(view.label, view.tone), h("p", { class: "note" }, view.note)),
    h("td", {}, row.sentAt === null ? "not sent" : instantCell(row.sentAt)),
    h("td", {}, instantCell(row.expiry)),
  );
}

/** Mount the notification settings and delivery view. */
export function mountNotifications(
  ctx: ViewContext,
  services: DeliveryServices = servicesOf(ctx, window),
): ViewHandle {
  const frame = makeFrame("notifications", "Notifications");
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const settings = h("section", { attrs: { "aria-labelledby": "quiet-title" } });
  const deliveries = h("section", { attrs: { "aria-labelledby": "deliveries-title" } });
  let quiet: QuietHoursRow | null = null;
  let dialog: DialogHandle | null = null;
  ctx.scope.add(() => {
    dialog?.close();
  });

  const drawSettings = (): void => {
    const problem = h("p", { class: "form-problem", attrs: { role: "alert" } });
    const enabled = h("input", { attrs: { type: "checkbox", name: "enabled" } });
    enabled.checked = quiet?.enabled ?? false;
    const start = h("input", {
      attrs: { type: "time", name: "start", value: clockText(quiet?.startMinute ?? 0) },
    });
    const end = h("input", {
      attrs: { type: "time", name: "end", value: clockText(quiet?.endMinute ?? 0) },
    });
    const reason = h("input", {
      attrs: { type: "text", name: "reason", value: "", autocomplete: "off" },
    });
    const save = h("button", { attrs: { type: "submit" } }, "Review and save…");
    const form = h(
      "form",
      { attrs: { "aria-label": "Quiet hours", novalidate: "" } },
      h("label", {}, enabled, " Hold non-critical messages during quiet hours"),
      h("label", {}, "From (IST) ", start),
      h("label", {}, "Until (IST) ", end),
      h("label", {}, "Why you are changing it ", reason),
      save,
      problem,
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const from = parseClock(start.value);
      const until = parseClock(end.value);
      if (from === null || until === null || reason.value.trim() === "") {
        problem.textContent = "Enter both times as HH:MM and say why you are changing them.";
        return;
      }
      problem.textContent = "";
      // One key per intent: the same window re-uses it until answered; another window is a new one.
      const request = {
        clientKey: "",
        enabled: enabled.checked,
        startMinute: from,
        endMinute: until,
        reason: reason.value.trim(),
      };
      dialog?.close();
      dialog = openConfirmDialog(
        ctx.commands.keys,
        {
          intentId: `quiet-hours|${JSON.stringify(encodeQuietHoursRequest(request))}`,
          id: "save-quiet-hours",
          title: "Save quiet hours?",
          summary: [
            [
              "Quiet hours",
              enabled.checked ? `${clockText(from)} to ${clockText(until)} IST` : "off",
            ],
            ["Held", "Non-critical messages only"],
            ["Never held", `${NOT_SILENCEABLE.join(" and ")} escalation`],
          ],
          confirmLabel: "Save quiet hours",
          run: async (clientKey) => {
            const answer = await services.post(
              QUIET_HOURS_PATH,
              encodeQuietHoursRequest({ ...request, clientKey }),
              (json) => decodeQuietHoursRow(json),
            );
            if (answer === null) {
              return "The view changed; nothing was sent.";
            }
            quiet = answer;
            drawSettings();
            return "Saved. Mandatory escalation is unchanged.";
          },
        },
        save,
      );
    });
    setChildren(settings, [
      h("h2", { attrs: { id: "quiet-title" } }, "Quiet hours"),
      h(
        "p",
        { class: "window", attrs: { "data-window": "true" } },
        quiet === null ? "Quiet hours are not set." : windowText(quiet),
      ),
      h(
        "p",
        { class: "escalation", attrs: { "data-escalation": "locked" } },
        h("input", {
          attrs: { type: "checkbox", disabled: "", "aria-label": "Silence mandatory escalation" },
        }),
        " Mandatory escalation (SEV1 and mandatory messages) cannot be silenced. It is always sent.",
      ),
      form,
    ]);
  };

  const drawDeliveries = (rows: readonly DeliveryRow[]): void => {
    setChildren(deliveries, [
      h("h2", { attrs: { id: "deliveries-title" } }, "Delivery states"),
      h(
        "p",
        { class: "note" },
        "Accepted by the provider is not delivered: only a message with a verified provider callback shows Delivered.",
      ),
      rows.length === 0
        ? h("p", {}, "No notifications yet.")
        : scrollRegion(
            "Recent notifications",
            h(
              "table",
              { class: "grid" },
              h("caption", {}, "Recent notifications"),
              h(
                "thead",
                {},
                h(
                  "tr",
                  {},
                  ...["Message", "Channel", "Urgency", "State", "Sent", "Expires"].map((name) =>
                    h("th", { attrs: { scope: "col" } }, name),
                  ),
                ),
              ),
              h("tbody", {}, ...rows.map(deliveryRow)),
            ),
          ),
    ]);
  };

  drawSettings();
  drawDeliveries([]);
  frame.body.append(settings, deliveries);
  void ctx.client
    .read("notifications", decodeNotificationsRow)
    .then((answer) => {
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      const rows = answer.page.rows;
      quiet = rows.find((row): row is QuietHoursRow => row.kind === "quiet_hours") ?? null;
      const state = stateOfEnvelope(answer);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
      ]);
      drawSettings();
      drawDeliveries(rows.filter((row): row is DeliveryRow => row.kind === "delivery"));
    })
    .catch((error: unknown) => {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    });
  return handleOf(frame, ctx);
}
